import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { Package, RefreshCw, Clock, CheckCircle } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ReturnPolicy = () => {
  const [customContent, setCustomContent] = useState(null);

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const res = await axios.get(`${API_URL}/api/settings`);
        if (res.data.return_policy_content) {
          setCustomContent(res.data.return_policy_content);
        }
      } catch (e) {
        console.error('Failed to fetch settings:', e);
      }
    };
    fetchSettings();
  }, []);

  return (
    <div className="min-h-screen bg-[#f0f4f8]">
      <Navbar />
      
      {/* Hero Section */}
      <div className="pt-44 pb-12 bg-gradient-to-br from-[#003456] to-[#001d30]">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <div className="w-20 h-20 bg-[#fdc31c] rounded-full flex items-center justify-center mx-auto mb-6">
            <RefreshCw className="h-10 w-10 text-[#003456]" />
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">Return Policy</h1>
          <p className="text-xl text-white/80 max-w-2xl mx-auto">
            We stand behind our products. If you're not completely satisfied, we're here to help.
          </p>
        </div>
      </div>
      
      <div className="py-16">
        <div className="max-w-4xl mx-auto px-6">
          {customContent ? (
            <div className="bg-white rounded-2xl shadow-sm p-8 md:p-12 prose prose-lg max-w-none">
              <div className="text-gray-600" dangerouslySetInnerHTML={{ __html: customContent }} />
            </div>
          ) : (
            <>
              {/* Quick Overview */}
              <div className="grid md:grid-cols-3 gap-6 mb-12">
                {[
                  { icon: Clock, title: '30 Days', subtitle: 'Return Window', description: 'From delivery date' },
                  { icon: Package, title: 'Free', subtitle: 'Defect Returns', description: 'For manufacturing defects' },
                  { icon: CheckCircle, title: 'Full Refund', subtitle: 'Unopened Items', description: 'Original condition only' }
                ].map((item, i) => (
                  <div key={i} className="bg-white rounded-2xl p-6 text-center shadow-sm">
                    <item.icon className="h-10 w-10 text-[#003456] mx-auto mb-3" />
                    <p className="text-2xl font-bold text-[#003456]">{item.title}</p>
                    <p className="text-lg font-medium text-[#0a0203] mb-1">{item.subtitle}</p>
                    <p className="text-gray-500 text-sm">{item.description}</p>
                  </div>
                ))}
              </div>
              
              <div className="bg-white rounded-2xl shadow-sm p-8 md:p-12">
                <h2 className="text-2xl font-bold text-[#0a0203] mb-6">Return Policy Details</h2>
                
                <div className="space-y-8">
                  <div>
                    <h3 className="text-xl font-semibold text-[#003456] mb-3">Eligibility for Returns</h3>
                    <p className="text-gray-600 mb-4">Products may be returned within 30 days of delivery if:</p>
                    <ul className="list-disc pl-6 text-gray-600 space-y-2">
                      <li>The item is in its original, unopened packaging</li>
                      <li>The item has a manufacturing defect</li>
                      <li>The item arrived damaged during shipping</li>
                      <li>You received the wrong item</li>
                    </ul>
                  </div>

                  <div>
                    <h3 className="text-xl font-semibold text-[#003456] mb-3">Non-Returnable Items</h3>
                    <ul className="list-disc pl-6 text-gray-600 space-y-2">
                      <li>Items used more than once for testing purposes</li>
                      <li>Items without original packaging or documentation</li>
                      <li>Accessories with broken seals</li>
                      <li>Custom-configured products (unless defective)</li>
                    </ul>
                  </div>

                  <div>
                    <h3 className="text-xl font-semibold text-[#003456] mb-3">Return Shipping</h3>
                    <div className="grid md:grid-cols-2 gap-4">
                      <div className="bg-green-50 rounded-lg p-4">
                        <h4 className="font-semibold text-green-800 mb-2">Free Return Shipping</h4>
                        <ul className="text-sm text-green-700 space-y-1">
                          <li>Manufacturing defects</li>
                          <li>Damaged during shipping</li>
                          <li>Wrong item received</li>
                        </ul>
                      </div>
                      <div className="bg-gray-50 rounded-lg p-4">
                        <h4 className="font-semibold text-gray-800 mb-2">Customer Pays Shipping</h4>
                        <ul className="text-sm text-gray-700 space-y-1">
                          <li>Change of mind</li>
                          <li>Ordered wrong size/model</li>
                          <li>No longer needed</li>
                        </ul>
                      </div>
                    </div>
                  </div>

                  <div>
                    <h3 className="text-xl font-semibold text-[#003456] mb-3">How to Return</h3>
                    <ol className="space-y-3 text-gray-600">
                      <li className="flex gap-3">
                        <span className="w-6 h-6 bg-[#003456] text-white rounded-full flex items-center justify-center text-sm flex-shrink-0">1</span>
                        <span>Contact us at <a href="mailto:info@peakprairie.ca" className="text-[#003456] hover:underline">info@peakprairie.ca</a> to initiate a return</span>
                      </li>
                      <li className="flex gap-3">
                        <span className="w-6 h-6 bg-[#003456] text-white rounded-full flex items-center justify-center text-sm flex-shrink-0">2</span>
                        <span>Receive your Return Merchandise Authorization (RMA) number</span>
                      </li>
                      <li className="flex gap-3">
                        <span className="w-6 h-6 bg-[#003456] text-white rounded-full flex items-center justify-center text-sm flex-shrink-0">3</span>
                        <span>Pack the item securely in its original packaging</span>
                      </li>
                      <li className="flex gap-3">
                        <span className="w-6 h-6 bg-[#003456] text-white rounded-full flex items-center justify-center text-sm flex-shrink-0">4</span>
                        <span>Ship to our return center (we'll provide a prepaid label if eligible)</span>
                      </li>
                      <li className="flex gap-3">
                        <span className="w-6 h-6 bg-[#003456] text-white rounded-full flex items-center justify-center text-sm flex-shrink-0">5</span>
                        <span>Refund processed within 5-7 business days of receiving the item</span>
                      </li>
                    </ol>
                  </div>

                  <div className="bg-[#dbeef5] rounded-xl p-6">
                    <h3 className="text-lg font-semibold text-[#003456] mb-2">Regional Information</h3>
                    <p className="text-gray-600">
                      Return policies apply equally to customers in <strong>Canada</strong> and the <strong>United States</strong>. 
                      Return shipping labels will be provided for your country of residence. Cross-border returns may be subject 
                      to additional customs processing time.
                    </p>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
      
      <Footer />
    </div>
  );
};

export default ReturnPolicy;
